import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { fadeUp, viewportOnce, prefersReducedMotion } from '../theme';
import { useTenantTheme } from '../TenantThemeContext';

/**
 * RevealOnScroll — animates children into view once.
 * Falls back to no-op when the user prefers reduced motion.
 */
export function RevealOnScroll({
    as: Tag = 'div',
    variants = fadeUp,
    delay = 0,
    className = '',
    children,
    ...rest
}) {
    const reduce = prefersReducedMotion();
    if (reduce) {
        return <Tag className={className} {...rest}>{children}</Tag>;
    }
    const MotionTag = motion[Tag] || motion.div;
    return (
        <MotionTag
            className={className}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            variants={variants}
            transition={{ delay }}
            {...rest}
        >
            {children}
        </MotionTag>
    );
}

/**
 * GlassCard — frosted glass surface that picks up theme colors.
 * Optional `ribbon` prop renders a corner badge.
 */
export function GlassCard({
    className = '',
    ribbon,
    children,
    interactive = false,
    style,
    ...rest
}) {
    return (
        <div
            className={[
                'relative rounded-2xl border overflow-hidden',
                'bg-[var(--tenant-surface-alt)]/70 backdrop-blur-xl',
                'border-[var(--tenant-border)]',
                'shadow-[0_2px_30px_-12px_rgba(0,0,0,0.45)]',
                interactive ? 'transition-transform duration-300 will-change-transform' : '',
                className
            ].join(' ')}
            style={style}
            {...rest}
        >
            {ribbon && (
                <div className="absolute top-3 right-3 z-10 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-semibold">
                    {ribbon}
                </div>
            )}
            {children}
        </div>
    );
}

/**
 * TiltCard — pointer-tracked 3D tilt wrapper.
 */
export function TiltCard({ className = '', children, max = 6, ...rest }) {
    const reduce = prefersReducedMotion();
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotX = useSpring(useTransform(y, [-0.5, 0.5], [max, -max]), {
        stiffness: 220,
        damping: 20
    });
    const rotY = useSpring(useTransform(x, [-0.5, 0.5], [-max, max]), {
        stiffness: 220,
        damping: 20
    });

    function handleMove(e) {
        if (reduce) return;
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        x.set(px);
        y.set(py);
    }
    function handleLeave() {
        x.set(0);
        y.set(0);
    }

    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            style={
                reduce
                    ? undefined
                    : { rotateX: rotX, rotateY: rotY, transformStyle: 'preserve-3d' }
            }
            className={['will-change-transform', className].join(' ')}
            {...rest}
        >
            {children}
        </motion.div>
    );
}

/**
 * MagneticButton — attracts the cursor for a subtle premium feel.
 * Renders as a real <button> or <a>.
 */
export function MagneticButton({
    as = 'button',
    href,
    onClick,
    variant = 'primary',
    size = 'md',
    className = '',
    children,
    icon,
    ...rest
}) {
    const reduce = prefersReducedMotion();
    const ref = useRef(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    function handleMove(e) {
        if (reduce) return;
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const px = e.clientX - rect.left - rect.width / 2;
        const py = e.clientY - rect.top - rect.height / 2;
        setPos({ x: px * 0.06, y: py * 0.08 });
    }
    function handleLeave() {
        setPos({ x: 0, y: 0 });
    }

    const sizing = {
        sm: 'h-10 px-4 text-sm',
        md: 'h-12 px-6 text-sm',
        lg: 'h-14 px-8 text-base'
    }[size];

    const variants = {
        primary:
            'bg-[var(--tenant-primary)] text-[var(--tenant-surface)] hover:brightness-110',
        secondary:
            'bg-transparent text-[var(--tenant-ink)] border border-[var(--tenant-border)] hover:bg-[var(--tenant-surface-alt)]',
        ghost:
            'bg-transparent text-[var(--tenant-ink)] hover:text-[var(--tenant-primary)]'
    }[variant];

    // Resolve element/component:
    //  - string ('a','button',etc) → motion.<tag>
    //  - React component (e.g. react-router <Link>) → motion(Component)
    //  - href present without explicit `as` → anchor
    const Tag = React.useMemo(() => {
        if (typeof as === 'string') {
            return motion[as] || motion.button;
        }
        if (as) {
            return motion(as);
        }
        return href ? motion.a : motion.button;
    }, [as, href]);

    return (
        <Tag
            ref={ref}
            href={href}
            onClick={onClick}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            animate={{ x: pos.x, y: pos.y }}
            transition={{ type: 'spring', stiffness: 220, damping: 26 }}
            className={[
                'inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-wide uppercase',
                'transition-colors duration-300 select-none',
                sizing,
                variants,
                className
            ].join(' ')}
            {...rest}
        >
            {icon}
            <span className="relative">{children}</span>
        </Tag>
    );
}

/**
 * AmbientBlob — animated radial gradient blob, color sourced from theme.
 * Use multiple in a section for atmospheric depth.
 */
export function AmbientBlob({
    color,
    size = 460,
    className = '',
    style,
    delay = 0
}) {
    return (
        <motion.div
            aria-hidden
            className={['pointer-events-none absolute rounded-full blur-3xl', className].join(' ')}
            style={{
                width: size,
                height: size,
                background: color || 'var(--tenant-blob-a)',
                ...style
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.85, scale: 1 }}
            transition={{ duration: 2.4, delay, ease: [0.22, 1, 0.36, 1] }}
        />
    );
}

/**
 * GridBackdrop — subtle grid line backdrop.
 */
export function GridBackdrop({ className = '', opacity = 0.06 }) {
    return (
        <div
            aria-hidden
            className={[
                'pointer-events-none absolute inset-0',
                className
            ].join(' ')}
            style={{
                backgroundImage:
                    'linear-gradient(var(--tenant-border) 1px, transparent 1px),' +
                    'linear-gradient(90deg, var(--tenant-border) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                opacity
            }}
        />
    );
}

/**
 * SectionHeader — template-aware. Five visual variants driven by
 * `template.tokens.headerVariant`:
 *   centered  – Forest: bar + display heading + soft tagline
 *   stamped   – Workshop: oversized number/eyebrow stamp + uppercase
 *   minimal   – Showroom: tracked-out tiny eyebrow + clean ink heading
 *   underlined – Express: chevron + primary underline
 *   serif     – Heritage: italic heading + ornament rule
 */
export function SectionHeader({
    eyebrow,
    title,
    titleBn,
    body,
    bodyBn,
    align = 'left',
    className = '',
    stamp
}) {
    const { template } = useTenantTheme();
    const tk = template?.tokens || {};
    const variant = tk.headerVariant || 'centered';
    const headingCls = tk.headerHeading || 'font-display text-4xl md:text-6xl leading-[0.95]';
    const eyebrowCls = tk.eyebrowStyle || 'tracking-[0.32em]';
    const alignCls = align === 'center' ? 'text-center mx-auto' : '';

    const heading = (
        <h2 className={[headingCls, 'text-[var(--tenant-ink)]'].join(' ')}>
            {title}
        </h2>
    );

    const titleBnNode = titleBn && (
        <h3 className="font-bn text-2xl md:text-3xl mt-2 text-[var(--tenant-accent)]/90">
            {titleBn}
        </h3>
    );

    const bodyNodes = (
        <>
            {body && (
                <p className="mt-4 text-base md:text-lg text-[var(--tenant-muted)] max-w-2xl">
                    {body}
                </p>
            )}
            {bodyBn && (
                <p className="mt-2 font-bn text-base text-[var(--tenant-muted)] max-w-2xl">
                    {bodyBn}
                </p>
            )}
        </>
    );

    if (variant === 'stamped') {
        return (
            <div className={['max-w-3xl relative', alignCls, className].join(' ')}>
                {eyebrow && (
                    <div className={['inline-flex items-center gap-2 text-[10px] uppercase font-bold mb-3 text-[var(--tenant-primary)]', eyebrowCls].join(' ')}>
                        <span className="h-[2px] w-8 bg-[var(--tenant-primary)]" />
                        {eyebrow}
                    </div>
                )}
                <div className="relative">
                    {heading}
                </div>
                {titleBnNode}
                <div className="mt-4 h-[3px] w-16 bg-[var(--tenant-ink)]" />
                {bodyNodes}
            </div>
        );
    }

    if (variant === 'minimal') {
        return (
            <div className={['max-w-3xl', alignCls, className].join(' ')}>
                {eyebrow && (
                    <div className={['text-[10px] uppercase mb-6 text-[var(--tenant-muted)]', eyebrowCls].join(' ')}>
                        {eyebrow}
                    </div>
                )}
                {heading}
                {titleBnNode}
                {bodyNodes}
            </div>
        );
    }

    if (variant === 'underlined') {
        return (
            <div className={['max-w-3xl', alignCls, className].join(' ')}>
                {eyebrow && (
                    <div className={['inline-flex items-center gap-2 text-[11px] uppercase font-bold mb-3', eyebrowCls].join(' ')}>
                        <span className="inline-block w-1.5 h-1.5 rotate-45 bg-[var(--tenant-primary)]" />
                        {eyebrow}
                    </div>
                )}
                {heading}
                <div className="mt-2 h-1 w-24 rounded-full bg-[var(--tenant-primary)]" />
                {titleBnNode}
                {bodyNodes}
            </div>
        );
    }

    if (variant === 'serif') {
        return (
            <div className={['max-w-3xl', alignCls, className].join(' ')}>
                {eyebrow && (
                    <div className={['inline-flex items-center gap-3 text-[11px] uppercase mb-5 text-[var(--tenant-primary)]', eyebrowCls].join(' ')}>
                        <span className="h-px w-8 bg-[var(--tenant-primary)]/60" />
                        {eyebrow}
                        <span className="h-px w-8 bg-[var(--tenant-primary)]/60" />
                    </div>
                )}
                {heading}
                {titleBnNode}
                <div className={['mt-5 flex items-center gap-3', align === 'center' ? 'justify-center' : ''].join(' ')}>
                    <span className="h-px w-10 bg-[var(--tenant-border)]" />
                    <span className="text-[var(--tenant-primary)]">✦</span>
                    <span className="h-px w-10 bg-[var(--tenant-border)]" />
                </div>
                {bodyNodes}
            </div>
        );
    }

    // centered (default — Forest)
    return (
        <div className={['max-w-3xl', alignCls, className].join(' ')}>
            {eyebrow && (
                <div className={['inline-flex items-center gap-2 text-[11px] uppercase text-[var(--tenant-primary)] mb-4', eyebrowCls].join(' ')}>
                    <span className="h-px w-6 bg-[var(--tenant-primary)]" />
                    {eyebrow}
                </div>
            )}
            {heading}
            {titleBnNode}
            {bodyNodes}
        </div>
    );
}

/**
 * TemplateCard — feature/highlight card whose look swaps per template.
 *   glass    – Forest: frosted GlassCard
 *   numbered – Workshop: hard edges, top stripe, big numeric badge
 *   outline  – Showroom: hairline outline, generous whitespace
 *   solid    – Express: primary-soft fill, energetic
 *   classic  – Heritage: warm beige with thicker border + corner ornament
 */
export function TemplateCard({
    index = 0,
    icon: Icon,
    title,
    titleBn,
    body,
    bodyBn,
    languageBn = false,
    className = '',
    children
}) {
    const { template } = useTenantTheme();
    const tk = template?.tokens || {};
    const variant = tk.cardVariant || 'glass';
    const hover = tk.cardHover || '';
    const num = String(index + 1).padStart(2, '0');

    const titleNode = title && (
        <h3 className="font-headline font-semibold text-lg text-[var(--tenant-ink)]">{title}</h3>
    );
    const titleBnNode = titleBn && (
        <h4 className="font-bn text-base mt-1 text-[var(--tenant-accent)]/90">{titleBn}</h4>
    );
    const bodyNode = body && (
        <p className="text-sm text-[var(--tenant-muted)] mt-2 leading-relaxed">{body}</p>
    );
    const bodyBnNode = bodyBn && (
        <p className="font-bn text-sm mt-1 text-[var(--tenant-muted)]/90">{bodyBn}</p>
    );

    if (variant === 'numbered') {
        return (
            <div className={['relative bg-[var(--tenant-surface)] border border-[var(--tenant-border)] rounded-none p-6 transition-colors duration-200', hover, className].join(' ')}>
                <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--tenant-primary)]" />
                <div className="flex items-start justify-between gap-3">
                    <span className="font-display text-3xl text-[var(--tenant-primary)]">{num}</span>
                    {Icon && <Icon className="w-6 h-6 text-[var(--tenant-ink)]/70" />}
                </div>
                <div className="mt-4">
                    {titleNode}
                    {titleBnNode}
                    {bodyNode}
                    {bodyBnNode}
                    {children}
                </div>
            </div>
        );
    }

    if (variant === 'outline') {
        return (
            <div className={['relative border border-[var(--tenant-border)] rounded-2xl p-8 transition-colors duration-300', hover, className].join(' ')}>
                {Icon && <Icon className="w-7 h-7 text-[var(--tenant-primary)] mb-6" />}
                {titleNode}
                {titleBnNode}
                {bodyNode}
                {bodyBnNode}
                {children}
            </div>
        );
    }

    if (variant === 'solid') {
        return (
            <div className={['relative rounded-xl p-5 bg-[var(--tenant-primary-soft)] border border-[var(--tenant-primary)]/15 transition-all duration-200', hover, className].join(' ')}>
                <div className="flex items-center gap-2 text-[var(--tenant-primary)] font-bold text-xs">
                    <span className="inline-block w-5 h-5 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] flex items-center justify-center text-[10px] font-display">{num}</span>
                    {Icon && <Icon className="w-4 h-4" />}
                </div>
                <div className="mt-3">
                    {titleNode}
                    {titleBnNode}
                    {bodyNode}
                    {bodyBnNode}
                    {children}
                </div>
            </div>
        );
    }

    if (variant === 'classic') {
        return (
            <div className={['relative bg-[var(--tenant-surface-alt)] border-2 border-[var(--tenant-border)] rounded-md p-6 transition-shadow duration-300', hover, className].join(' ')}>
                <span aria-hidden className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[var(--tenant-primary)]" />
                <span aria-hidden className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-[var(--tenant-primary)]" />
                {Icon && <Icon className="w-6 h-6 text-[var(--tenant-primary)] mb-3" />}
                {titleNode}
                {titleBnNode}
                {bodyNode}
                {bodyBnNode}
                {children}
            </div>
        );
    }

    // glass — Forest default
    return (
        <GlassCard className={['p-6 transition-all duration-300', hover, className].join(' ')}>
            {Icon && (
                <div className="w-11 h-11 rounded-xl bg-[var(--tenant-primary-soft)] text-[var(--tenant-primary)] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                </div>
            )}
            {titleNode}
            {titleBnNode}
            {bodyNode}
            {bodyBnNode}
            {children}
        </GlassCard>
    );
}

/**
 * TemplateDivider — slim section divider whose form follows the active template.
 */
export function TemplateDivider({ className = '' }) {
    const { template } = useTenantTheme();
    const variant = template?.tokens?.divider || 'bracket';

    if (variant === 'stripe') {
        return (
            <div className={['max-w-7xl mx-auto px-5 md:px-8 py-3', className].join(' ')}>
                <div className="flex items-center gap-2">
                    <span className="h-[3px] flex-1 bg-[var(--tenant-ink)]" />
                    <span className="h-[3px] w-8 bg-[var(--tenant-primary)]" />
                </div>
            </div>
        );
    }
    if (variant === 'hairline') {
        return (
            <div className={['max-w-7xl mx-auto px-5 md:px-8', className].join(' ')}>
                <div className="h-px bg-[var(--tenant-border)]" />
            </div>
        );
    }
    if (variant === 'ticker') {
        return (
            <div className={['relative overflow-hidden bg-[var(--tenant-primary)] text-[var(--tenant-surface)] py-2', className].join(' ')}>
                <div className="flex gap-8 whitespace-nowrap animate-[tickerScroll_28s_linear_infinite] text-xs font-bold tracking-[0.3em] uppercase">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <span key={i} className="flex items-center gap-3">FAST SERVICE <span>•</span> ON-TIME DELIVERY <span>•</span> WARRANTY ASSURED <span>•</span></span>
                    ))}
                </div>
            </div>
        );
    }
    if (variant === 'ornament') {
        return (
            <div className={['max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-center gap-4', className].join(' ')}>
                <span className="h-px w-24 bg-[var(--tenant-border)]" />
                <span className="text-[var(--tenant-primary)] text-lg">❦</span>
                <span className="h-px w-24 bg-[var(--tenant-border)]" />
            </div>
        );
    }
    // bracket — Forest default
    return (
        <div className={['max-w-7xl mx-auto px-5 md:px-8 py-2 flex items-center gap-3', className].join(' ')}>
            <span className="h-px w-10 bg-[var(--tenant-primary)]" />
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--tenant-primary)]" />
            <span className="h-px flex-1 bg-[var(--tenant-border)]" />
        </div>
    );
}

/**
 * useLenis — initialize smooth scroll once at site root.
 * Disabled when user prefers reduced motion or template motion is 'low'/'none'.
 */
export function useLenis(enabled = true) {
    useEffect(() => {
        if (!enabled) return undefined;
        if (typeof window === 'undefined') return undefined;
        if (prefersReducedMotion()) return undefined;

        let lenis;
        let rafId;
        let cancelled = false;

        // Lazy import to keep initial bundle small.
        import('lenis').then(({ default: Lenis }) => {
            if (cancelled) return;
            lenis = new Lenis({
                duration: 1.05,
                smoothWheel: true,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
            });
            const raf = (time) => {
                lenis.raf(time);
                rafId = requestAnimationFrame(raf);
            };
            rafId = requestAnimationFrame(raf);
        }).catch(() => {
            // Lenis import failed — silently fall back to native scroll.
        });

        return () => {
            cancelled = true;
            if (rafId) cancelAnimationFrame(rafId);
            if (lenis) lenis.destroy();
        };
    }, [enabled]);
}
